/**
 * Small pure string formatters shared across the browser-shared layer and the
 * SPA. Keeping them free of React/DOM lets the fast unit project import them
 * directly while the SPA bundles them unchanged.
 */

/**
 * Avatar fallback initials for a person or agent name. Uses the first and last
 * word ("John Michael Smith" → "JS"), the standard avatar convention, so the
 * same name renders the same initials on every surface (chat, participant
 * stacks, specialist and team cards). A single word yields its first two
 * letters ("Solo" → "SO"); an empty/whitespace name yields "?".
 */
export function initials(name: string): string {
  const trimmed = name.trim();
  if (trimmed.length === 0) return "?";
  const words = trimmed.split(/\s+/);
  if (words.length === 1) {
    return (words[0] ?? "").slice(0, 2).toUpperCase();
  }
  const first = words[0] ?? "";
  const last = words[words.length - 1] ?? "";
  return `${first.charAt(0)}${last.charAt(0)}`.toUpperCase();
}

/**
 * Title-cases a slug or free-text label by word ("memory-handbook" → "Memory
 * Handbook", "weekly report" → "Weekly Report"). Splits on hyphen, underscore,
 * and whitespace so kebab/snake slugs and space-separated titles both read as
 * separate words; only the first character of each word is forced upper, so
 * intentional inner capitals survive. Callers whose tokens carry extra
 * delimiters (tool ids also break on ":" and ".") pass a wider `separators`
 * pattern; the default preserves the kebab/snake/space behaviour.
 */
export function titleCaseWords(value: string, separators: RegExp = /[-_\s]+/): string {
  return value
    .split(separators)
    .filter((word) => word.length > 0)
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(" ");
}

/**
 * A skill's display name, and the single place that decision is made so no two
 * surfaces can disagree about what one skill is called.
 *
 * A bundled skill's name is the slug in its `SKILL.md` frontmatter, so it is
 * title-cased for display. A catalog skill's name is free text its author
 * typed — hyphens included — and renders exactly as written; rewriting it would
 * show an operator a different name than the skill has.
 */
export function skillDisplayName(name: string, source: "bundled" | "catalog" | undefined): string {
  return source === "catalog" ? name : titleCaseWords(name);
}
